import React, { useContext, useEffect, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { IAppState } from 'src/store';
import paymentAPI from 'src/pages/deprecated/payment/api';
import history from 'src/lib/history';
import {
  CUSTOMER_BILLING_ADDRESS,
  CUSTOMER_SHIPPING_ADDRESS,
} from 'src/pages/constants';
import ProductService from 'src/services/product-service';
import { ToastContext } from 'src/components/toast/toast';
import {
  trackPostPurchaseUpsellViewed,
  trackPostPurchaseUpsellDeclined,
  trackPostPurchaseAddonSelected,
} from 'src/util/track';
import ChallengeNutritionUpsellModal from 'src/pages/payment-new/modal/ChallengeNutritionUpsellModal';
import Loader from 'src/components/content/loader';

interface LocationState {
  successState: Record<string, any>;
  nutritionCoachingPriceId: string;
  nutritionCoachingPrice: number;
}

const ChallengeBundleCoachingUpsell = () => {
  const user = useSelector((state: IAppState) => state.auth?.user);
  const { addToast } = useContext(ToastContext);
  const stripe = useStripe();

  const locationState = history.location.state as LocationState | undefined;
  const successState = locationState?.successState;
  const nutritionCoachingPriceId =
    locationState?.nutritionCoachingPriceId ?? '';
  const nutritionCoachingPrice = locationState?.nutritionCoachingPrice ?? 0;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!successState) {
      history.replace('/signup');
      return;
    }
    trackPostPurchaseUpsellViewed('nutrition_coaching', successState.priceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToSuccess = (coachingAdded = false) => {
    const url = `/success?ref=TransactionComplete&isChallengeFlow=true${
      coachingAdded ? '&nutritionCoachingAdded=true' : ''
    }`;
    history.push(url, successState);
  };

  const handleDecline = () => {
    if (isSubmitting) return;
    trackPostPurchaseUpsellDeclined(
      'nutrition_coaching',
      successState?.priceId
    );
    navigateToSuccess(false);
  };

  const handleConfirmCoaching = async () => {
    if (!stripe || !user || !nutritionCoachingPriceId) {
      setErrorMessage('Unable to process. Please try again.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const { data } = await paymentAPI.post('stripe/customer/create', {
        email: user.email,
        userID: user.id,
      });

      const customerId = data.customer?.id;
      if (!customerId) throw new Error('Failed to retrieve customer');

      const customerResponse = await paymentAPI.get(
        'stripe/customer-my-account',
        {
          params: { email: user.email },
        }
      );
      const subscriptionId =
        customerResponse?.data?.customer?.subscriptions?.data?.[0]?.id;

      const purchaseResponse = await paymentAPI.post(
        'stripe/invoice/purchase-addons',
        {
          customerID: customerId,
          subscriptionID: subscriptionId,
          paymentMethodID: undefined,
          priceIDs: [nutritionCoachingPriceId],
          userID: user.id,
          isAfterPay: true,
          isCardPayment: false,
        }
      );

      if (!purchaseResponse.data.isSuccess) {
        throw new Error(purchaseResponse.data.message || 'Purchase failed');
      }

      if (!purchaseResponse.data.requiresPaymentCompletion) {
        trackPostPurchaseAddonSelected(
          'nutrition_coaching',
          successState?.priceId
        );
        navigateToSuccess(true);
        return;
      }

      const { clientSecret } = purchaseResponse.data;

      localStorage.setItem(
        'payObj',
        JSON.stringify({
          type: 'challengeCoachingAddon',
          priceId: nutritionCoachingPriceId,
          successState,
        })
      );

      const result = await stripe.confirmAfterpayClearpayPayment(clientSecret, {
        return_url: `${window.location.origin}/confirmAfterPay`,
        mandate_data: await ProductService.getCustomerAcceptance(),
        payment_method: CUSTOMER_BILLING_ADDRESS,
        shipping: CUSTOMER_SHIPPING_ADDRESS,
      });

      if (result.error) {
        setErrorMessage('Something went wrong. Please try again.');
        localStorage.setItem('payObj', '{}');
        setIsSubmitting(false);
      }
      // On success, redirect happens — return URL is handled in Afterpay.tsx
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Payment failed. Please try again.';
      setErrorMessage(message);
      addToast(message, 'danger', 5000);
      localStorage.setItem('payObj', '{}');
      setIsSubmitting(false);
    }
  };

  if (!successState) {
    return <Loader />;
  }

  return (
    <ChallengeNutritionUpsellModal
      open={true}
      price={nutritionCoachingPrice}
      isSubmitting={isSubmitting}
      errorMessage={errorMessage}
      onDecline={handleDecline}
      onConfirmPurchase={handleConfirmCoaching}
    />
  );
};

export default ChallengeBundleCoachingUpsell;
